import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/context/ThemeContext";
import {
  LayoutDashboard, PhoneCall, Building2, Radio, ArrowDownToLine, ArrowUpFromLine,
  ListTree, Users2, Rows3, PhoneIncoming, ScrollText, Settings, FileCode2, LogOut, Menu, X,
} from "lucide-react";
import { useState } from "react";

export function Logo({ size = 38 }) {
  return (
    <div className="flex items-center gap-3" data-testid="brand-logo">
      <div
        style={{ width: size, height: size }}
        className="rounded-xl grid place-items-center border border-[hsl(var(--gold)/0.4)] bg-[hsl(var(--gold)/0.08)] shadow-[0_0_24px_-8px_hsl(var(--gold)/0.6)]"
      >
        <span className="gold-text font-display font-extrabold" style={{ fontSize: size * 0.42 }}>IC</span>
      </div>
      <div className="leading-tight">
        <div className="font-display font-bold tracking-tight text-[15px]">InContact</div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Cloud PBX</div>
      </div>
    </div>
  );
}

const NAV = [
  { section: "Overview", items: [{ to: "/", icon: LayoutDashboard, label: "Dashboard", end: true }] },
  {
    section: "Telephony",
    items: [
      { to: "/extensions", icon: PhoneCall, label: "Extensions" },
      { to: "/departments", icon: Building2, label: "Departments" },
      { to: "/trunks", icon: Radio, label: "SIP Trunks" },
    ],
  },
  {
    section: "Routing",
    items: [
      { to: "/inbound", icon: ArrowDownToLine, label: "Inbound Routes" },
      { to: "/outbound", icon: ArrowUpFromLine, label: "Outbound Routes" },
    ],
  },
  {
    section: "Call Flows",
    items: [
      { to: "/ivr", icon: ListTree, label: "IVR / Auto-Attendant" },
      { to: "/ring-groups", icon: Rows3, label: "Ring Groups" },
      { to: "/queues", icon: PhoneIncoming, label: "Call Queues" },
    ],
  },
  {
    section: "System",
    items: [
      { to: "/call-logs", icon: ScrollText, label: "Call Logs (CDR)" },
      { to: "/users", icon: Users2, label: "Users" },
      { to: "/config", icon: FileCode2, label: "Config Export" },
      { to: "/settings", icon: Settings, label: "Settings" },
    ],
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); nav("/login"); };

  const SideContent = (
    <div className="flex h-full flex-col">
      <div className="px-5 py-5 border-b border-border"><Logo /></div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {NAV.map((grp) => (
          <div key={grp.section}>
            <div className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">
              {grp.section}
            </div>
            <div className="space-y-1">
              {grp.items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                  data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                >
                  <it.icon size={17} strokeWidth={2} />
                  <span>{it.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-[hsl(var(--gold)/0.15)] border border-[hsl(var(--gold)/0.35)] grid place-items-center font-semibold text-[hsl(var(--gold))]">
            {(user?.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground capitalize">{user?.role}</div>
          </div>
          <ThemeToggle />
          <button onClick={doLogout} data-testid="logout-btn"
            className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
            <LogOut size={17} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[268px] shrink-0 glass border-r flex-col">{SideContent}</aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="relative w-[268px] glass border-r">{SideContent}</aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden flex items-center justify-between px-4 h-14 glass border-b sticky top-0 z-40">
          <Logo size={30} />
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <button onClick={() => setOpen(!open)} className="p-2" data-testid="mobile-menu-btn">
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </header>
        <main className="flex-1 p-5 md:p-8 max-w-[1400px] w-full mx-auto" key={loc.pathname}>
          <div className="animate-fade-up"><Outlet /></div>
        </main>
      </div>
    </div>
  );
}
